import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface ActionHistory {
  actionId: number;
  userId: number;
  actionType: string;
  description: string;
  entityType: string;
  entityId: number;
  createdAt: string;
  additionalData?: any;
  user?: {
    userId: number;
    userName: string;
    email: string;
  };
}

export interface PackagerPackagingKpi {
  packager: string;
  packagesCount: number;
  sparePartsCount: number;
  averagePartsPerPackage: number;
  dailyProductivityPackages: number;
}

export interface PackagingKpiResponse {
  fromDate?: string;
  toDate?: string;
  packager?: string;
  totalPackages: number;
  totalSpareParts: number;
  averagePartsPerPackage: number;
  dailyProductivityPackages: number;
  packagers: PackagerPackagingKpi[];
}

export interface DailyPackagingKpiRow {
  date: string;
  packager: string;
  packagesCount: number;
  sparePartsCount: number;
  averagePartsPerPackage: number;
}

export interface DailyPackagingTotalByDate {
  date: string;
  packagesCount: number;
  sparePartsCount: number;
  averagePartsPerPackage: number;
}

export interface PackagingDailyKpiResponse {
  fromDate?: string;
  toDate?: string;
  packager?: string;
  rows: DailyPackagingKpiRow[];
  totalsByDate: DailyPackagingTotalByDate[];
}

@Injectable({
  providedIn: 'root'
})
export class ActionHistoryService {
  private apiUrl = environment.apiEndpoint + '/api/ActionHistory';

  constructor(private http: HttpClient) {
  }

  getPaginatedActionHistory(params: {
    page: number;
    pageSize: number;
    search?: string;
    actionType?: string;
    entityType?: string;
    date?: string;
  }): Observable<any> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('pageSize', params.pageSize.toString());
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.actionType) httpParams = httpParams.set('actionType', params.actionType);
    if (params.entityType) httpParams = httpParams.set('entityType', params.entityType);
    if (params.date) httpParams = httpParams.set('date', params.date);
    return this.http.get<any>(`${this.apiUrl}`, { params: httpParams }).pipe(
      catchError(error => {
        console.error('Error al obtener historial paginado:', error);
        return throwError(() => error);
      })
    );
  }

  getActionTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/types`).pipe(
      catchError(error => {
        console.error('Error al obtener tipos de acción:', error);
        return throwError(() => error);
      })
    );
  }

  getAllActions(): Observable<ActionHistory[]> {
    return this.http.get<ActionHistory[]>(`${this.apiUrl}/all`).pipe(
      catchError(error => {
        console.error('Error al obtener todas las acciones:', error);
        return throwError(() => error);
      })
    );
  }

  getSummary(startDate?: string, endDate?: string): Observable<any> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<any>(`${this.apiUrl}/summary`, { params }).pipe(
      catchError(error => {
        console.error('Error al obtener el resumen:', error);
        return throwError(() => error);
      })
    );
  }

  getById(id: number): Observable<ActionHistory> {
    return this.http.get<ActionHistory>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Error al obtener acción por ID:', error);
        return throwError(() => error);
      })
    );
  }

  getEntityOrderInfo(entityType: string, entityId: number): Observable<{ orderNumber: string; siniestro: string }> {
    const params = new HttpParams()
      .set('entityType', entityType)
      .set('entityId', entityId.toString());
    return this.http.get<{ orderNumber: string; siniestro: string }>(
      `${this.apiUrl}/entity-order-info`,
      { params }
    ).pipe(
      catchError(error => {
        console.error('Error al obtener orden/siniestro:', error);
        return throwError(() => error);
      })
    );
  }

  getPackagingKpis(params: { fromDate?: string; toDate?: string; packager?: string }): Observable<PackagingKpiResponse> {
    let httpParams = new HttpParams();

    if (params.fromDate) {
      httpParams = httpParams.set('fromDate', params.fromDate);
    }
    if (params.toDate) {
      httpParams = httpParams.set('toDate', params.toDate);
    }
    if (params.packager) {
      httpParams = httpParams.set('packager', params.packager);
    }

    return this.http.get<PackagingKpiResponse>(`${this.apiUrl}/packaging-kpis`, { params: httpParams }).pipe(
      catchError(error => {
        console.error('Error al obtener KPIs de empaquetado:', error);
        return throwError(() => error);
      })
    );
  }

  getPackagingDailyKpis(params: { fromDate?: string; toDate?: string; packager?: string }): Observable<PackagingDailyKpiResponse> {
    let httpParams = new HttpParams();

    if (params.fromDate) {
      httpParams = httpParams.set('fromDate', params.fromDate);
    }
    if (params.toDate) {
      httpParams = httpParams.set('toDate', params.toDate);
    }
    if (params.packager) {
      httpParams = httpParams.set('packager', params.packager);
    }

    return this.http.get<PackagingDailyKpiResponse>(`${this.apiUrl}/packaging-kpis/daily`, { params: httpParams }).pipe(
      catchError(error => {
        console.error('Error al obtener KPIs diarios de empaquetado:', error);
        return throwError(() => error);
      })
    );
  }
}
